import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { motion } from "framer-motion";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import Chatbot from "../components/Chatbot";
import JsonLd from "../components/JsonLd";
import QuoteFormMini from "../components/QuoteFormMini";
import { SERVICES, getServiceBySlug } from "../lib/services";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  useEffect(() => {
    if (service) document.title = `${service.title} · Greenroute`;
    window.scrollTo(0, 0);
  }, [service]);

  if (!service) return <Navigate to="/" replace />;

  const Icon = service.icon;
  const related = SERVICES.filter((s) => s.slug !== service.slug).slice(0, 3);

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: service.title,
    description: service.intro,
    provider: {
      "@type": "LocalBusiness",
      name: "Greenroute Estate Management Services",
      telephone: "+27661833893",
      email: "admin@green-route.co.za",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Cnr John & Earp Street, Selby",
        addressLocality: "Johannesburg",
        addressRegion: "Gauteng",
        postalCode: "2001",
        addressCountry: "ZA",
      },
    },
    areaServed: ["Gauteng", "Western Cape", "KwaZulu-Natal", "Eastern Cape"],
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      name: service.title,
      itemListElement: service.deliverables.map((d) => ({
        "@type": "Offer",
        itemOffered: { "@type": "Service", name: d },
      })),
    },
  };

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://green-route.co.za/" },
      { "@type": "ListItem", position: 2, name: "Services", item: "https://green-route.co.za/#services" },
      { "@type": "ListItem", position: 3, name: service.title, item: `https://green-route.co.za/services/${service.slug}` },
    ],
  };

  return (
    <main className="bg-cream" data-testid="service-detail-page">
      <JsonLd id="service-jsonld" data={serviceJsonLd} />
      <JsonLd id="breadcrumb-jsonld" data={breadcrumbJsonLd} />
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-forest text-cream relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-10" />
        <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
          <Link
            to="/#services"
            className="inline-flex items-center gap-2 text-cream/70 hover:text-cream text-sm tracking-wide mb-10 link-underline"
            data-testid="back-to-services"
          >
            <ArrowLeft size={16} /> All services
          </Link>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <Icon size={42} strokeWidth={1.25} className="text-gold mb-8" />
              <div className="uppercase text-xs tracking-[0.3em] text-gold mb-6">
                Greenroute Services
              </div>
              <h1
                className="font-serif text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight"
                data-testid="service-title"
              >
                {service.title}
              </h1>
            </div>
            <div className="lg:col-span-4 lg:col-start-9">
              <p className="text-cream/85 text-base md:text-lg leading-relaxed">
                {service.short}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Image */}
      {service.image && (
        <div className="max-w-7xl mx-auto px-6 md:px-12 -mt-12 md:-mt-16 relative z-10">
          <motion.img
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            src={service.image}
            alt={service.title}
            className="w-full h-[420px] md:h-[520px] object-cover"
          />
        </div>
      )}

      {/* Body */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-16">
          <div className="lg:col-span-7">
            <div className="uppercase text-xs tracking-[0.3em] text-sage mb-6">
              About this service
            </div>
            <p className="font-serif text-3xl md:text-4xl text-forest leading-snug">
              {service.intro}
            </p>
            <div className="divider-line my-10" />
            <div className="uppercase text-[10px] tracking-[0.25em] text-sage mb-6">
              What's included
            </div>
            <ul className="space-y-4" data-testid="service-deliverables">
              {service.deliverables.map((d) => (
                <li key={d} className="flex items-start gap-4 text-ink">
                  <span className="w-6 h-6 bg-forest text-cream flex items-center justify-center shrink-0 mt-0.5">
                    <Check size={14} strokeWidth={2.5} />
                  </span>
                  <span className="text-base md:text-lg leading-relaxed">{d}</span>
                </li>
              ))}
            </ul>
          </div>

          <aside className="lg:col-span-4 lg:col-start-9">
            <div className="sticky top-28 space-y-6" data-testid="service-sidebar">
              <QuoteFormMini service={service} />
              <div className="bg-forest text-cream p-6">
                <div className="uppercase text-[10px] tracking-[0.25em] text-gold mb-3">
                  Ideal for
                </div>
                <p className="font-serif text-lg leading-snug">{service.ideal_for}</p>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Related */}
      <section className="py-20 md:py-28 bg-[#F2F0EA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-6">
            <h2 className="font-serif text-3xl md:text-5xl text-forest leading-tight">
              Explore other services
            </h2>
            <Link
              to="/#services"
              className="text-sm tracking-wide text-forest link-underline"
              data-testid="all-services-link"
            >
              View all →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {related.map((r) => {
              const RIcon = r.icon;
              return (
                <Link
                  key={r.slug}
                  to={`/services/${r.slug}`}
                  data-testid={`related-${r.slug}`}
                  className="bg-cream border border-bordersoft p-8 group hover:bg-white hover:border-forest transition-all duration-500"
                >
                  <RIcon size={28} strokeWidth={1.25} className="text-forest mb-6 group-hover:text-gold transition-colors" />
                  <h3 className="font-serif text-2xl text-ink leading-tight mb-3">
                    {r.title}
                  </h3>
                  <p className="text-graphite text-sm leading-relaxed mb-6">
                    {r.short}
                  </p>
                  <span className="inline-flex items-center gap-2 text-sm text-forest">
                    Read more <ArrowRight size={14} />
                  </span>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
      <Chatbot />
    </main>
  );
}
